import { useEffect, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/useToast';
import { getScrollTooLongToast } from '@/lib/copyToasts';

const SCROLL_THRESHOLD_MS = 60000; // 1 minute
const IDLE_TIMEOUT_MS = 2000; // 2 seconds of no scroll = reset timer
const STORAGE_KEY = 'espy-scroll-toast-shown';

/**
 * Hook that tracks continuous scrolling and shows a one-time toast
 * if the user scrolls for more than a minute without stopping.
 */
export function useScrollTimer() {
  const { toast } = useToast();
  const scrollStartTime = useRef<number | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownToast = useRef<boolean>(false);

  // Check if we've already shown the toast in a previous session
  useEffect(() => {
    const shown = localStorage.getItem(STORAGE_KEY);
    hasShownToast.current = shown === 'true';
  }, []);

  const resetTimer = useCallback(() => {
    scrollStartTime.current = null;
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }, []);

  const handleScroll = useCallback(() => {
    // Don't track if we've already shown the toast
    if (hasShownToast.current) return;

    // Start timer if not already started
    if (scrollStartTime.current === null) {
      scrollStartTime.current = Date.now();
    }

    // Clear existing idle timer
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }

    // Check if we've exceeded the threshold
    const elapsed = Date.now() - scrollStartTime.current;
    if (elapsed >= SCROLL_THRESHOLD_MS) {
      // Show the toast
      toast({ title: getScrollTooLongToast() });
      hasShownToast.current = true;
      localStorage.setItem(STORAGE_KEY, 'true');
      resetTimer();
      return;
    }

    // Set idle timer - if no scroll for 2 seconds, reset the timer
    idleTimer.current = setTimeout(() => {
      resetTimer();
    }, IDLE_TIMEOUT_MS);
  }, [toast, resetTimer]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
    };
  }, [handleScroll]);
}
